import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NotFound from "../../../notfound/NotFound";
import {
  hasPermission,
  privilegesEnum,
} from "../../../../helpers/PermissionHelper";

import { Form, Input, InputNumber, message, Switch, Skeleton, Button, Select } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { RxListBullet } from "react-icons/rx";
import { editRewardType, getRewardTypeById } from "../../../../api/Services";
import CustomBreadcrumb from "../../../../components/breadcrumb/Breadcrumb";
import ActionButtons from "../../../../components/actionbuttons/ActionButtons";
import { getRewardTypesBasePath } from "../../../../helpers/RewardsPathHelper";

import styles from "./ViewRewardType.module.scss";
import {
  REWARD_VALUE_TYPE_NOMINAL,
  rewardValueTypeSelectOptions,
} from "../rewardValueTypes";

const ViewRewardType = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const getBackUrl = () => {
    const queryString = new URLSearchParams(location.search).toString();
    return `${getRewardTypesBasePath(location.pathname)}${queryString ? `?${queryString}` : ""}`;
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rewardType, setRewardType] = useState<any>();

  const [editForm] = Form.useForm();

  const canEdit = hasPermission(privilegesEnum.WRITE_REWARDS);

  const { id } = useParams();

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_REWARDS)) {
      handleFetchRewardType();
    }
  }, []);

  const handleFetchRewardType = async () => {
    let response = await getRewardTypeById(id!);
    setRewardType(response);
    setLoading(false);
  };

  useEffect(() => {
    const typeData = rewardType?.data ?? rewardType;
    if (!typeData) return;
    const status = typeData.status === true || typeData.status === "true";
    editForm.setFieldsValue({
      name: typeData.name,
      status,
      values:
        typeData.values?.map((v: any) => ({
          value: v.value,
          valueType: v.valueType ?? REWARD_VALUE_TYPE_NOMINAL,
        })) ?? [],
    });
  }, [rewardType, editForm]);

  const handleEdit = async () => {
    if (!id) return;

    setSaving(true);
    const { name, status, values: valuesList } = editForm.getFieldsValue();
    const rows =
      valuesList?.filter(
        (x: { value?: number }) =>
          x?.value != null && !Number.isNaN(x.value)
      ) ?? [];
    const payloadValues = rows.map(
      (x: { value: number; valueType?: string }) => ({
        value: x.value,
        valueType: x.valueType ?? REWARD_VALUE_TYPE_NOMINAL,
      })
    );

    let response = await editRewardType(id, {
      name,
      status,
      values: payloadValues,
    });
    if (response) {
      message.success(t("Common-EditSuccessMessage"));
      await handleFetchRewardType();
    } else message.error(t("Common-EditErrorMessage"));
    setSaving(false);
  };

  const handleCancelButton = () => {
    navigate(getBackUrl());
  };

  return loading ? (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <Skeleton.Input style={{ width: 180, height: 20 }} active />
        </div>
        <div className={styles.actionButtonsWrapper}>
          <Skeleton.Button style={{ width: 80, height: 32 }} active />
          <Skeleton.Button style={{ width: 80, height: 32 }} active />
        </div>
      </div>

      <div className={styles.rewardTypeInfoWrapper}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    </div>
  ) : !rewardType ? (
    <NotFound />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxListBullet />,
                label: t("Common-RewardTypes"),
                path: getBackUrl()
              },
              {
                label:
                  (rewardType?.data ?? rewardType)?.name ||
                  t("Common-RewardType"),
                isCurrentPage: true
              }
            ]}
          />
        </div>
        <ActionButtons
          onSave={handleEdit}
          onCancel={handleCancelButton}
          loading={saving}
          disabled={saving}
          show={true}
          permission={privilegesEnum.WRITE_REWARDS}
        />
      </div>

      <div className={styles.rewardTypeInfoWrapper}>
        <Form
          name="editRewardType"
          form={editForm}
          autoComplete="off"
          className={styles.newForm}
          layout="vertical"
          preserve={false}
        >
          <div className={styles.itemWrapper}>
            <Form.Item
              label={t("Common-Name")}
              name="name"
              className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: t("Common-Name"),
                },
              ]}
            >
              <Input disabled={!canEdit} placeholder={t("Common-Name")} />
            </Form.Item>
            <Form.Item
              label={t("Common-Active")}
              name="status"
              valuePropName="checked"
              className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: t("Common-Active"),
                },
              ]}
            >
              <Switch disabled={!canEdit} />
            </Form.Item>
          </div>
          <div className={styles.sectionTitle}>
            {t("Common-PossibleValues")}
          </div>
          <Form.List name="values">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name: fieldName, ...restField }) => (
                  <div key={key} className={styles.valuesRow}>
                    <Form.Item
                      {...restField}
                      name={[fieldName, "valueType"]}
                      initialValue={REWARD_VALUE_TYPE_NOMINAL}
                      rules={[
                        { required: true, message: t("Common-RequiredField") },
                      ]}
                      className={styles.valueTypeSelect}
                    >
                      <Select
                        options={rewardValueTypeSelectOptions(t)}
                        placeholder={t("RewardValueType-ValueKind")}
                        disabled={!canEdit}
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[fieldName, "value"]}
                      rules={[
                        { required: true, message: t("Common-Value") },
                      ]}
                      className={styles.formItem}
                    >
                      <InputNumber
                        className={styles.inputNumber}
                        min={0}
                        step={0.01}
                        placeholder={t("Common-Value")}
                        disabled={!canEdit}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                    {canEdit && (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(fieldName)}
                        className={styles.removeValueBtn}
                      />
                    )}
                  </div>
                ))}
                {canEdit && (
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() =>
                        add({ valueType: REWARD_VALUE_TYPE_NOMINAL })
                      }
                      block
                      icon={<PlusOutlined />}
                      className={styles.addValueBtn}
                    >
                      {t("Common-Add")} {t("Common-Value")}
                    </Button>
                  </Form.Item>
                )}
              </>
            )}
          </Form.List>
        </Form>
      </div>
    </div>
  );
};

export default ViewRewardType;
